import { useEffect, useState } from 'react';
import PageContentWrapper from 'src/components/PageContentWrapper/PageContentWrapper';
import { CreateProjectStepsTitle } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectStepsTitle/CreateProjectStepsTitle';
import { Button } from 'src/components/ui/button';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from 'src/store';
import {
  useGetConfigurationSettingsPromptQuery,
  useGetDefaultConfigurationTemplateFieldsQuery,
  useUpdateConfigurationSettingsPromptMutation,
} from 'src/services';
import { useLoader } from 'src/provider/LoaderProvider';
import { setCreationFlow } from 'src/store/slices/creationFlowSlices';
import { FormProvider, useForm } from 'react-hook-form';
import { formResolver } from 'src/utils/formResolver';
import { booleanScheme, stringScheme } from 'src/utils/validation-schemes';
import { CreateProjectHead } from 'src/pages/ProjectPage/CreateProjectSteps/CreateProjectHead/CreateProjectHead';
import { Skeleton } from 'src/components/ui/skeleton';
import * as yup from 'yup';
import { ChannelAccordion } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep5/ChannelAccordion/ChannelAccordion';
import { StyleAccordion } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep5/StyleAccordion/StyleAccordion';
import { ResponseLengthAccordion } from 'src/pages/OnboardingPage/OnboardingSteps/OnboardingStep5/ResponseLengthAccordion/ResponseLengthAccordion';
import { API_VERSION } from 'src/constants/api-routes';
import { useCreationFlowSettings } from 'src/pages/ProjectPage/CreateProjectSteps/useCreationFlowSettings';
import { getCreateProjectStepPath } from 'src/pages/ProjectPage/CreateProjectSteps/createProjectStepPath';
import type {
  ConfigurationFormFieldsObject,
  IndexedFormField,
} from 'src/pages/ConfigurationPage/hooks/useConfigurationFormFields';
import {
  toFormFieldsObject,
  withFieldIndex,
} from 'src/pages/ConfigurationPage/hooks/useConfigurationFormFields';
import type { ToneOfVoiceFormValues } from 'src/pages/ConfigurationPage/utils/getPersonaFormValues';
import { getApiVersion } from 'src/utils/apiVersion';

const LoadingSkeleton = () => {
  return (
    <div className={'w-[455px] flex flex-col gap-5'}>
      <Skeleton className={'w-full h-4 mb-10'} />
      <Skeleton className={'w-full h-20'} />
      <Skeleton className={'w-full h-96'} />
    </div>
  );
};

const CreateProjectStep5Page = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [inputData, setInputData] = useState(null);
  const [formFields, setFormFields] = useState<IndexedFormField[] | null>(null);
  const [formFieldsObject, setFormFieldsObject] = useState<ConfigurationFormFieldsObject>({});
  const [accordionValue, setAccordionValue] = useState('');
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const { id, step } = useParams();
  const loader = useLoader();
  const navigate = useNavigate();
  const { projectType } = useAppSelector((s) => s.projectsSlices);
  const projectTypeVar = projectType || type;
  const creationFlowState = useAppSelector((s) => s.creationFlowSlices);
  const dispatch = useAppDispatch();
  const version = getApiVersion();
  const isV2 = version === API_VERSION.V2;
  const { data: defaultTemplateFields, isLoading: isLoadingDefaultTemplateFields } =
    useGetDefaultConfigurationTemplateFieldsQuery();
  const { settings, activeSettings, isLoadingAllConfigurationSettings } = useCreationFlowSettings(
    id,
    { skip: !(id && defaultTemplateFields), refetchOnMountOrArgChange: true },
  );
  const [
    updateConfigurationSettings,
    { data: updateConfigurationSettingsData, isLoading: isLoadingUpdateConfigurationSettings },
  ] = useUpdateConfigurationSettingsPromptMutation();
  const { data: configurationSettingsPrompt, isLoading: isLoadingConfigurationSettingsPrompt } =
    useGetConfigurationSettingsPromptQuery(
      { project_id: id, settings_id: activeSettings?.id },
      {
        skip: !(id && activeSettings && defaultTemplateFields),
        refetchOnMountOrArgChange: true,
      },
    );

  useEffect(() => {
    if (
      isLoadingDefaultTemplateFields ||
      isLoadingAllConfigurationSettings ||
      isLoadingConfigurationSettingsPrompt ||
      isLoadingUpdateConfigurationSettings ||
      !configurationSettingsPrompt ||
      settings === null
    ) {
      setIsLoading(true);
      loader.show();
    } else {
      setIsLoading(false);
      loader.hide();
    }
  }, [
    isLoadingAllConfigurationSettings,
    isLoadingDefaultTemplateFields,
    isLoadingConfigurationSettingsPrompt,
    isLoadingUpdateConfigurationSettings,
    configurationSettingsPrompt,
    settings,
  ]);

  useEffect(() => {
    if (defaultTemplateFields) {
      const fields = withFieldIndex(defaultTemplateFields.form_fields);
      setFormFields(fields);
      setInputData(null);
    }
  }, [defaultTemplateFields]);

  useEffect(() => {
    if (formFields) {
      const fieldsObject = toFormFieldsObject(formFields);

      setFormFieldsObject(fieldsObject);
    }
  }, [formFields]);

  useEffect(() => {
    if (configurationSettingsPrompt) {
      const fields = withFieldIndex(configurationSettingsPrompt.form_fields);

      setFormFields(fields);
      setInputData(configurationSettingsPrompt.input_data);
    }
  }, [configurationSettingsPrompt]);

  useEffect(() => {
    if (inputData) {
      methods.reset({
        pf_tone_of_voice_channel:
          inputData?.pf_tone_of_voice_channel ||
          formFieldsObject?.pf_tone_of_voice_channel?.default_value?.value ||
          'chat',
        pf_tone_of_voice_style:
          inputData?.pf_tone_of_voice_style ||
          formFieldsObject?.pf_tone_of_voice_style?.default_value?.value ||
          'casual',
        pf_tone_of_voice_resp_len:
          inputData?.pf_tone_of_voice_resp_len ||
          formFieldsObject?.pf_tone_of_voice_resp_len?.default_value?.value ||
          'concise',
        pf_tone_of_voice_style_custom: inputData?.pf_tone_of_voice_style_custom || '',
        pf_tone_of_voice_emoji:
          inputData?.pf_tone_of_voice_emoji ||
          formFieldsObject?.pf_tone_of_voice_emoji?.default_value?.value ||
          false,
      });
    }
  }, [inputData]);

  useEffect(() => {
    if (updateConfigurationSettingsData) {
      next();
    }
  }, [updateConfigurationSettingsData]);

  useEffect(() => {
    const config = creationFlowState[id];

    if (config && +config.step < +step) {
      dispatch(
        setCreationFlow({
          id: id,
          step: step,
        }),
      );
    }
  }, []);

  const methods = useForm<ToneOfVoiceFormValues>({
    defaultValues: {
      pf_tone_of_voice_channel:
        inputData?.pf_tone_of_voice_channel ||
        formFieldsObject?.pf_tone_of_voice_channel?.default_value?.value ||
        'chat',
      pf_tone_of_voice_style:
        inputData?.pf_tone_of_voice_style ||
        formFieldsObject?.pf_tone_of_voice_style?.default_value?.value ||
        'casual',
      pf_tone_of_voice_resp_len:
        inputData?.pf_tone_of_voice_resp_len ||
        formFieldsObject?.pf_tone_of_voice_resp_len?.default_value?.value ||
        'concise',
      pf_tone_of_voice_style_custom: inputData?.pf_tone_of_voice_style_custom || '',
      pf_tone_of_voice_emoji:
        inputData?.pf_tone_of_voice_emoji !== undefined
          ? inputData?.pf_tone_of_voice_emoji
          : formFieldsObject?.pf_tone_of_voice_emoji?.default_value?.value || false,
    },
    resolver: formResolver(
      yup.lazy((values) => {
        const baseSchema = {
          pf_tone_of_voice_emoji: booleanScheme(),
          pf_tone_of_voice_channel: stringScheme({
            required: formFieldsObject?.pf_tone_of_voice_channel?.is_required || false,
          }).label('Tone of Voice Channel'),
          pf_tone_of_voice_style: stringScheme({
            required: formFieldsObject?.pf_tone_of_voice_style?.is_required || false,
          }).label('Tone of Voice Style'),
          pf_tone_of_voice_style_custom: stringScheme({
            required:
              formFieldsObject?.pf_tone_of_voice_style_custom?.is_required ||
              values.pf_tone_of_voice_style === 'custom',
          }).label('Tone of Voice Style - Custom'),
        };

        return yup.object(baseSchema);
      }),
    ),
  });

  const style = methods.watch('pf_tone_of_voice_style');

  const submit = (data: ToneOfVoiceFormValues) => {
    if (activeSettings) {
      const body = {
        input_data: {
          ...inputData,
          ...data,
          pf_tone_of_voice_emoji: ['casual', 'custom'].includes(style)
            ? data.pf_tone_of_voice_emoji
            : false,
          pf_tone_of_voice_style_custom:
            style === 'custom' ? data.pf_tone_of_voice_style_custom : null,
        },
      };

      updateConfigurationSettings({ project_id: id, settings_id: activeSettings.id, body });
    }
  };

  const next = () => {
    navigate(getCreateProjectStepPath(id, +step + 1, projectTypeVar), { replace: true });
  };

  const back = () => {
    navigate(getCreateProjectStepPath(id, +step - 1, projectTypeVar), { replace: true });
  };

  return (
    <PageContentWrapper
      menuBarVisible={false}
      className={'create-project-step items-center !gap-5'}
    >
      {isLoading ? (
        <LoadingSkeleton />
      ) : (
        <>
          <CreateProjectStepsTitle step={isV2 ? +step - 1 : +step} title={'Tone of Voice'} />
          <CreateProjectHead step={+step} titleIndex={5} />
          <div className={'flex flex-col items-center w-[455px] gap-5'}>
            <FormProvider {...methods}>
              <form
                id={'create-project-step-5'}
                className={'flex flex-col gap-5 w-full'}
                onSubmit={methods.handleSubmit(submit)}
              >
                <ChannelAccordion
                  accordionValue={accordionValue}
                  setAccordionValue={setAccordionValue}
                  options={formFieldsObject?.pf_tone_of_voice_channel?.options}
                />
                <StyleAccordion
                  accordionValue={accordionValue}
                  setAccordionValue={setAccordionValue}
                  options={formFieldsObject?.pf_tone_of_voice_style?.options}
                />
                <ResponseLengthAccordion
                  accordionValue={accordionValue}
                  setAccordionValue={setAccordionValue}
                  options={formFieldsObject?.pf_tone_of_voice_resp_len?.options}
                />
              </form>
            </FormProvider>
            <Button
              form="create-project-step-5"
              type="submit"
              className="min-w-24 w-full flex flex-row h-12 mt-5"
            >
              Continue
            </Button>
            <button
              type="button"
              onClick={back}
              className={'text-[#3F60B3] text-xs font-medium cursor-pointer hover:underline'}
            >
              ← Back
            </button>
          </div>
        </>
      )}
    </PageContentWrapper>
  );
};

export default CreateProjectStep5Page;
